/**
 * TrustRoomNav.jsx
 * GRANULLO_TRUST_ROOM_ROLE_EXPANSION_ZERO_COMPROMISE_V1
 *
 * Canonical shared navigation for all Trust Room surfaces.
 * Adapts links and narrative based on active role.
 * Used by both /investor/* and /trust-room/:role/* routes.
 * One shell. Zero parallel drift.
 */
import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Target, BarChart3, TrendingUp, Shield, LogOut } from 'lucide-react';
import { useConvictionSpine } from '../contexts/StakeholderConvictionStateSpine';

const SURFACES = [
  { suffix: '', label: 'Overview', icon: null },
  { suffix: '/truth-tracking', label: 'Truth', icon: Target },
  { suffix: '/valuation-lab', label: 'Valuation', icon: BarChart3 },
  { suffix: '/conviction-replay', label: 'Replay', icon: TrendingUp },
  { suffix: '/board-observer', label: 'Board', icon: Shield },
];

export default function TrustRoomNav({ onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { activeRole, getRoleNarrative } = useConvictionSpine();

  // Detect base path: /investor or /trust-room/:role
  const isInvestorRoute = location.pathname.startsWith('/investor');
  const role = isInvestorRoute ? 'investor' : (params.role || activeRole);
  const basePath = isInvestorRoute ? '/investor' : `/trust-room/${role}`;

  const roleLabel = getRoleNarrative(role);

  return (
    <nav className="sticky top-0 z-40 px-6 lg:px-8"
      style={{
        background: 'var(--shell-bg)',
        borderBottom: '1px solid var(--g-border)',
        backdropFilter: 'blur(12px)',
      }}
      data-testid="trust-room-nav">
      <div className="max-w-[1100px] mx-auto flex items-center justify-between h-12">
        {/* Left: role label + surfaces */}
        <div className="flex items-center gap-1 overflow-x-auto">
          <span className="text-[9px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 rounded mr-2 shrink-0"
            style={{ background: 'rgba(0,0,0,0.04)', color: 'var(--shell-text-muted)' }}
            data-testid="trust-room-role-badge">
            {role.replace(/_/g, ' ')}
          </span>

          {SURFACES.map(s => {
            const path = `${basePath}${s.suffix}`;
            const isActive = s.suffix === ''
              ? location.pathname === basePath || location.pathname === `${basePath}/`
              : location.pathname.startsWith(path);
            const Icon = s.icon;

            return (
              <button key={s.suffix}
                onClick={() => navigate(path)}
                className="flex items-center gap-1 px-3 py-1.5 rounded text-[11px] font-mono uppercase tracking-[0.04em] whitespace-nowrap"
                style={{
                  background: isActive ? 'var(--shell-text)' : 'transparent',
                  color: isActive ? 'var(--shell-bg)' : 'var(--shell-text-muted)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.15s, color 0.15s',
                }}
                data-testid={`trust-nav-${s.label.toLowerCase()}`}>
                {Icon && <Icon size={11} />}
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Right: logout */}
        {onLogout && (
          <button onClick={onLogout}
            className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-mono uppercase shrink-0"
            style={{ background: 'none', border: '1px solid var(--g-border)', cursor: 'pointer', color: 'var(--shell-text-muted)' }}
            data-testid="trust-room-logout">
            <LogOut size={10} />
            Exit
          </button>
        )}
      </div>
    </nav>
  );
}
